import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface FillInBlankTapFieldsProps {
  textWithBlanks: string;
  correctAnswers: string[];
  words: string[];
  onChange: (updates: Partial<ActivityFormData>) => void;
  errors?: {
    textWithBlanks?: string;
    correctAnswers?: string;
    words?: string;
  };
}

export function FillInBlankTapFields({
  textWithBlanks,
  correctAnswers,
  words,
  onChange,
  errors,
}: FillInBlankTapFieldsProps) {
  const handleTextChange = (value: string) => {
    onChange({ textWithBlanks: value });
  };

  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...correctAnswers];
    newAnswers[index] = value;
    onChange({ correctAnswers: newAnswers });
  };

  const handleAddAnswer = () => {
    onChange({ correctAnswers: [...correctAnswers, ''] });
  };

  const handleRemoveAnswer = (index: number) => {
    if (correctAnswers.length <= 1) return;
    const newAnswers = correctAnswers.filter((_, i) => i !== index);
    onChange({ correctAnswers: newAnswers });
  };

  const handleWordChange = (index: number, value: string) => {
    const newWords = [...words];
    newWords[index] = value;
    onChange({ words: newWords });
  };

  const handleAddWord = () => {
    onChange({ words: [...words, ''] });
  };

  const handleRemoveWord = (index: number) => {
    if (words.length <= 1) return;
    const newWords = words.filter((_, i) => i !== index);
    onChange({ words: newWords });
  };

  const blankCount = (textWithBlanks.match(/_____/g) || []).length;

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Completar huecos con palabras (tap)</h3>
      <p className={styles.typeFields__description}>
        Estilo Duolingo: las palabras se muestran como tags abajo. El usuario pulsa una y se coloca
        en el hueco. Usa <strong>_____</strong> (5 guiones bajos) para los huecos.
      </p>

      <div>
        <label htmlFor="textWithBlanks" className={styles.typeFields__title}>
          Texto con huecos
        </label>
        <textarea
          id="textWithBlanks"
          value={textWithBlanks}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="Ejemplo: It is a beautiful _____ morning. Emma walks in the _____."
          className={styles.typeFields__textarea}
        />
        {blankCount > 0 && (
          <p className={styles.typeFields__hint}>
            {blankCount} {blankCount !== 1 ? 'huecos encontrados' : 'hueco encontrado'}
          </p>
        )}
        {errors?.textWithBlanks && (
          <span className={styles.typeFields__error}>{errors.textWithBlanks}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>Respuestas correctas ({correctAnswers.length})</label>
        <p className={styles.typeFields__description}>
          En el orden en que aparecen en el texto. Las correctas deben estar también en la lista de
          palabras.
        </p>
        <div className={styles.typeFields__list}>
          {correctAnswers.map((answer, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder={`Respuesta ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveAnswer(index)}
                disabled={correctAnswers.length <= 1}
                aria-label={`Eliminar respuesta ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddAnswer}
          className={styles.typeFields__addButton}
        >
          Añadir respuesta
        </Button>
        {errors?.correctAnswers && (
          <span className={styles.typeFields__error}>{errors.correctAnswers}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>Palabras a elegir ({words.length})</label>
        <p className={styles.typeFields__description}>
          Incluye las respuestas correctas más las distractoras. Se mostrarán como tags abajo.
        </p>
        <div className={styles.typeFields__list}>
          {words.map((word, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={word}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  placeholder={`Palabra ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveWord(index)}
                disabled={words.length <= 1}
                aria-label={`Eliminar palabra ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddWord}
          className={styles.typeFields__addButton}
        >
          Añadir palabra
        </Button>
        {errors?.words && <span className={styles.typeFields__error}>{errors.words}</span>}
      </div>
    </div>
  );
}
